import { Search, ThumbsUp, ThumbsDown } from 'lucide-react';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import MarkdownIt from 'markdown-it';
import styles from './ChatBody.module.css';
import { ChatSource, Message, toSource } from '../schemas';

type ChatBodyProps = {
  messages: Message[];
  isThinking?: boolean;
  isStreaming?: boolean;
  streamingContent?: string;
  processingStatus?: string;
  retrievalUrls?: ChatSource[];
  showPlaceholder?: boolean;
  onFeedback?: (msg: Message, value: 'up' | 'down') => void;
};

export const ChatBody: React.FC<ChatBodyProps> = ({
  messages,
  isThinking = false,
  isStreaming = false,
  streamingContent = '',
  processingStatus = '',
  retrievalUrls = [],
  showPlaceholder = true,
  onFeedback,
}) => {
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const renderMarkdown = (content: string) =>
    md.render(content)
      .replace(/&lt;br\s*\/?&gt;/gi, '<br>')
      // An empty cell (a row the model left without a source) reads as a deliberate blank, not a gap.
      .replace(/<td([^>]*)><\/td>/g, '<td$1>—</td>');

  // Initialize markdown-it instance
  const md = useMemo(() => {
    const instance = new MarkdownIt({
      html: false,
      linkify: true,
      typographer: true,
      breaks: true,
    });
    // A wide table scrolls inside its own frame. It used to sit in a clipped box, so the right-hand column
    // (Status, Source) was cut off mid-word on a narrow answer.
    instance.renderer.rules.table_open = () => `<div class="${styles.tableWrap}" data-testid="table-frame"><table>`;
    instance.renderer.rules.table_close = () => '</table></div>';
    return instance;
  }, []);
  
  // Auto-scroll to bottom when messages change, thinking state changes, or streaming content updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView();
  }, [messages, isThinking, isStreaming, streamingContent, retrievalUrls]);

  const toggleSourceExpansion = (messageId: number) => {
    setExpandedSources(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };
  
  const renderSources = (rawSources: ChatSource[], messageId: number, isExpanded: boolean) => {
    const sources = rawSources.map(toSource);
    return (
      <div className={styles.sourcesBubble}>
        <button 
          className={styles.sourcesHeader}
          onClick={() => toggleSourceExpansion(messageId)}
        >
          <div className={styles.sourcesHeaderLeft}>
            <span className={styles.sourcesIcon}><Search size={16} aria-hidden /></span>
            <span className={styles.sourcesTitle}>
              Searched {sources.length} source{sources.length !== 1 ? 's' : ''}
            </span>
          </div>
          <svg 
            className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}
            width="16" 
            height="16" 
            viewBox="0 0 16 16" 
            fill="none"
          >
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {isExpanded && (
          <div className={styles.sourcesContent}>
            <div className={styles.sourcesGrid}>
              {sources.map(({ url, title }, index) => {
                const hostname = new URL(url).hostname;
                const faviconUrl = `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
                
                return (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.sourceCard}
                    title={url}
                  >
                    <div className={styles.sourceCardContent}>
                      <img 
                        src={faviconUrl} 
                        alt={`${hostname} icon`}
                        className={styles.sourceFavicon}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = styles.sourceFaviconFallback;
                            fallback.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>';
                            parent.insertBefore(fallback, e.currentTarget);
                          }
                        }}
                      />
                      <div className={styles.sourceInfo}>
                        <div className={styles.sourceHostname}>{title || hostname}</div>
                        <div className={styles.sourceUrl}>{title ? hostname : url}</div>
                      </div>
                    </div>
                    <svg className={styles.externalLink} width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M11 7.5V11C11 11.2652 10.8946 11.5196 10.7071 11.7071C10.5196 11.8946 10.2652 12 10 12H3C2.73478 12 2.48043 11.8946 2.29289 11.7071C2.10536 11.5196 2 11.2652 2 11V4C2 3.73478 2.10536 3.48043 2.29289 3.29289C2.48043 3.10536 2.73478 3 3 3H6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 2H12V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 8L12 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className={styles.body}>
      <section className={styles.messagesArea}>
        <div className={styles.messagesList}>
          {messages.map((msg) => (
            <React.Fragment key={msg.id}>
              {msg.sender === 'user' ? (
                <div className={styles.userMessage}>
                  <div className={styles.messageBubble}>
                    {msg.attachmentName && (
                      <div className={styles.attachmentChip} title={msg.attachmentName}>
                        <span aria-hidden>📎</span>
                        <span className={styles.attachmentChipName}>{msg.attachmentName}</span>
                      </div>
                    )}
                    {msg.text}
                  </div>
                </div>
              ) : (
                <>
                  {msg.sources && msg.sources.length > 0 && (
                    <div className={styles.botMessage}>
                      {renderSources(msg.sources, msg.id, expandedSources.has(msg.id))}
                    </div>
                  )}
                  <div className={styles.botMessage}>
                    <div
                      className={`${styles.messageBubble} ${styles.markdown}`}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
                    />
                  </div>
                  {onFeedback && msg.dbId && (
                    <div className={styles.feedbackRow}>
                      <button
                        type="button"
                        className={`${styles.feedbackBtn} ${msg.feedback === 'up' ? styles.feedbackActive : ''}`}
                        onClick={() => onFeedback(msg, 'up')}
                        aria-pressed={msg.feedback === 'up'}
                        aria-label="Good response"
                        title="Good response"
                      >
                        <ThumbsUp size={15} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className={`${styles.feedbackBtn} ${msg.feedback === 'down' ? styles.feedbackActive : ''}`}
                        onClick={() => onFeedback(msg, 'down')}
                        aria-pressed={msg.feedback === 'down'}
                        aria-label="Bad response"
                        title="Bad response"
                      >
                        <ThumbsDown size={15} aria-hidden />
                      </button>
                    </div>
                  )}
                </>
              )}
            </React.Fragment>
          ))}
          
          {/* Show streaming content progressively */}
          {isStreaming && streamingContent && (
            <>
              {retrievalUrls.length > 0 && (
                <div className={styles.botMessage}>
                  {renderSources(retrievalUrls, -1, true)}
                </div>
              )}
              <div className={styles.botMessage}>
                <div 
                  className={`${styles.messageBubble} ${styles.markdown} ${styles.streaming}`}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingContent) }}
                />
              </div>
            </>
          )}
          
          {/* Show thinking/processing state */}
          {isThinking && !isStreaming && (
            <>
              {retrievalUrls.length > 0 && (
                <div className={styles.botMessage}>
                  {renderSources(retrievalUrls, -1, true)}
                </div>
              )}
              <div className={styles.botMessage}>
                <div className={styles.thinkingBubble}>
                  <div className={styles.thinkingHeader}>
                    <div className={styles.thinkingLoader}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className={styles.thinkingText}>
                      {processingStatus || 'Thinking...'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </section>
    </div>
  );
};
